import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button,
  Card,
  CardContent,
  Badge,
  toast 
} from '@etherisc/ui-kit';
import { productService } from '../../services/productService';
import { schemaService } from '../../services/schemaService';
import type { CreateProductRequest, ProductConfig } from '../../types/product';
import { PRODUCT_STATUS_LABELS } from '../../types/product';
import { ProductConfigForm } from '../../components/product/ProductConfigForm';
import { ChevronLeftIcon, FileTextIcon } from 'lucide-react';
import { ValidationErrorReport } from '../../components/common/ValidationErrorReport';
import { getErrorMessage } from '../../utils/errorHandling';
import { validateCoverageLevelLimits } from '../../utils/productValidation';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'DRAFT':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAUSED':
      return 'bg-orange-100 text-orange-800';
    case 'ARCHIVED':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getSchemaVersion = (productConfig: ProductConfig): string => {
  return productConfig?._version || 'v1.5.0';
};

export function CreateProduct() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    version: '1.0.0',
    tags: '',
    status: 'DRAFT' as 'ACTIVE' | 'DRAFT' | 'PAUSED' | 'ARCHIVED',
    quotationScript: '',
  });

  // Product config state (v1.5.0 structure)
  const [productConfig, setProductConfig] = useState<ProductConfig>({
    _version: '1.5.0',
    coverageConfig: {
    activitiesOrProducts: ['maize', 'sorghum', 'groundnuts'],
    perils: [], // Start empty - user will select from available perils
    areaOptions: ['Country-wide'],
    coveragePeriodLimits: {
      type: 'duration_days',
      minDurationDays: 90,
      maxDurationDays: 365,
    },
    sumInsuredLimits: {
      min: 500,
      max: 100000,
    },
    coverageLevelGlobalLimits: {},
    currency: 'USD' // Primary currency field
    },
    loadingConfig: {
    taxRate: 0.15,
    subsidyRate: 0.0,
      loadingFactors: [], // Start with empty array - user will add via UI
    perilWeighting: {
      enforcement: 'default',
      weights: {}
    },
            coverageLevelLimits: {
          enforcement: 'default',
          limits: {}
        },
    },
    stakeholdersAnrpDistribution: {
    administration: {
      insurer: {
        organizationId: '00000000-0000-0000-0000-000000000001', // Default placeholder - user will select
        name: 'Select Insurance Company',
        percentage: 0.8, // 80%
        role: 'Insurer',
      },
      myubuntu: {
        organizationId: '00000000-0000-0000-0000-000000000002', // Fixed MyUbuntu ID
        name: 'MyUbuntu Ltd',
        percentage: 0.15, // 15%
        role: 'MyUbuntu',
      },
    },
    underwriters: [
      {
        organizationId: '00000000-0000-0000-0000-000000000001', // Default to same as insurer
        name: 'Select Underwriter',
        percentage: 0.05, // 5%
        isInsurer: true,
      },
    ],
    brokers: [],
    calculatingAgents: [],
    otherOrganisations: [],
    },
    pricingFactors: {
      minimumPremium: 75,
      administrativeFee: 35,
      underwritingLoading: 0.03,
      agentCommissionRate: 0.12,
      brokerCommissionRate: 0.08,
      subsidyRate: 0,
    },
  });

  const [validationErrors, setValidationErrors] = useState<Array<{
    path: string;
    message: string;
    value: unknown;
  }>>([]);

  // Set current schema version on mount
  useEffect(() => {
    const setCurrentVersion = async () => {
      try {
        const currentVersion = await schemaService.getCurrentProductConfigVersion();
        setProductConfig(prev => ({
          ...prev,
          _version: currentVersion
        }));
      } catch (error) {
        console.error('Failed to get current schema version:', error);
        // Keep fallback version from initial state
      }
    };
    
    setCurrentVersion();
  }, []);

  const handleProductConfigChange = (newConfig: ProductConfig) => {
    console.log('🏗️ CreateProduct: Product config changed:', newConfig);
    console.log('🎯 CreateProduct: Loading factors:', newConfig.loadingConfig?.loadingFactors);
    setProductConfig(newConfig);
    // Clear validation errors when user makes changes
    setValidationErrors([]);
  };

  const validateForm = (): boolean => {
    const errors: Array<{ path: string; message: string; value: unknown }> = [];
    
    // Note: We don't clear backend validation errors here since they need to persist
    // until the user successfully submits or makes changes to the form

    // Validate basic form data
    if (!formData.name.trim()) {
      errors.push({
        path: 'name',
        message: 'Product name is required',
        value: formData.name
      });
    }

    if (!formData.code.trim()) {
      errors.push({
        path: 'code',
        message: 'Product code is required',
        value: formData.code
      });
    }

    // Validate product config
    const stakeholders = productConfig.stakeholdersAnrpDistribution;
    const totalPercentage = 
      stakeholders.administration.insurer.percentage +
      stakeholders.administration.myubuntu.percentage +
      stakeholders.underwriters.reduce((sum, u) => sum + u.percentage, 0) +
      (stakeholders.brokers?.reduce((sum, b) => sum + b.percentage, 0) || 0) +
      (stakeholders.calculatingAgents?.reduce((sum, c) => sum + c.percentage, 0) || 0) +
      (stakeholders.otherOrganisations?.reduce((sum, o) => sum + o.percentage, 0) || 0);

    if (Math.abs(totalPercentage - 1.0) > 0.001) {
      errors.push({
        path: 'stakeholdersAnrpDistribution',
        message: `Total ANRP percentage must equal 100%. Current: ${(totalPercentage * 100).toFixed(1)}%`,
        value: totalPercentage
      });
    }

    // Validate coverage level limits (#125, #126)
    errors.push(...validateCoverageLevelLimits(productConfig));

    // Check for missing organization IDs
    if (!stakeholders.administration.insurer.organizationId) {
      errors.push({
        path: 'stakeholdersAnrpDistribution.administration.insurer.organizationId',
        message: 'Please select an insurance organization',
        value: stakeholders.administration.insurer.organizationId
      });
    }

    if (!stakeholders.administration.myubuntu.organizationId) {
      errors.push({
        path: 'stakeholdersAnrpDistribution.administration.myubuntu.organizationId',
        message: 'Please select MyUbuntu organization',
        value: stakeholders.administration.myubuntu.organizationId
      });
    }

    setValidationErrors(errors);
    
    // Console logging for debugging
    if (errors.length > 0) {
      console.group('🔴 Product Creation Validation Errors');
      console.table(errors.map(err => ({
        Field: err.path,
        Message: err.message,
        Value: err.value
      })));
      console.groupEnd();
    }
    
    return errors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setLoading(true);
    try {
      const createRequest: CreateProductRequest = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        version: formData.version,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        status: formData.status,
        quotationScript: formData.quotationScript || undefined,
        productConfig: (() => {
          // Remove _version from productConfig before sending to backend (it's only for frontend validation)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { _version, ...productConfigForBackend } = productConfig;
          return productConfigForBackend;
        })()
      };



      const result = await productService.createProduct(createRequest);
      toast.success('Product created successfully!');
      navigate(`/admin/products/${result.product.id}`);
    } catch (error) {
      console.group('🚨 Product Creation Backend Error');
      console.error('Error creating product:', error);
      
      // Enhanced error logging for debugging
      if (error && typeof error === 'object' && 'data' in error) {
        const apiError = error as { data?: { error?: { message?: string; details?: { field?: string; value?: unknown; reason?: string; validationErrors?: unknown[] } } } };
        console.log('Full error object:', error);
        console.log('Error data:', apiError.data);
        
        const errorData = apiError.data;
        const backendError = errorData?.error;
        
        // Check for validation errors in the nested error structure first
        if (backendError?.details?.validationErrors) {
          const backendErrors = backendError.details.validationErrors.map((err: unknown) => {
            const errorObj = err as { field?: string; message?: string; schema?: string; data?: unknown };
            return {
              path: errorObj.field || 'unknown',
              message: errorObj.message || 'Validation error',
              schema: errorObj.schema,
              data: errorObj.data,
              value: errorObj.data
            };
          });
          
          console.log('🔍 Backend validation errors:', backendErrors);
          console.log('🔍 Raw validation errors from backend:', JSON.stringify(backendError.details.validationErrors, null, 2));
          
          // Combine with existing frontend validation errors
          setValidationErrors(prev => [...prev, ...backendErrors]);
          
          toast.error('Validation Failed', { 
            description: `Found ${backendErrors.length} validation errors. Please check the form below.` 
          });
        }
        // Check for AppError (e.g., duplicate product code) if no validation errors
        else if (backendError?.message) {
          const errorMessage = getErrorMessage(backendError.message);
          
          // If it's a field-specific error, add it to validation errors
          if (backendError.details?.field) {
            const fieldError = {
              path: backendError.details.field,
              message: errorMessage,
              value: backendError.details.value
            };
            setValidationErrors(prev => [...prev, fieldError]);
            toast.error('Validation Error', { description: errorMessage });
          } else {
            toast.error(errorMessage);
          }
          console.log('🔍 Backend error:', backendError);
        } else {
          console.log('❌ No validation errors found in expected structure');
          console.log('Available error data keys:', errorData ? Object.keys(errorData) : 'No data');
          console.log('Backend error:', backendError);
          toast.error('Failed to create product. Please try again.');
        }
      } else {
        console.log('❌ Error does not have expected structure');
        toast.error('Failed to create product. Please try again.');
      }
      console.groupEnd();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/products')}
          className="flex items-center gap-2"
        >
          <ChevronLeftIcon size={16} />
          Back to Products
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileTextIcon size={32} />
            {formData.name || 'Create New Product'}
          </h1>
          {formData.name && (
            <div className="text-gray-600 mt-1 flex items-center gap-4 text-sm">
              <span>{formData.code || 'No code yet'}</span>
              <span>•</span>
              <span>Version {formData.version || '1.0.0'}</span>
              <span>•</span>
              <span>New product</span>
              <Badge className={getStatusColor(formData.status)}>
                {PRODUCT_STATUS_LABELS[formData.status] || formData.status}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {getSchemaVersion(productConfig)}
              </Badge>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-8">
        {/* Product Configuration (v1.4.0) */}
        <ProductConfigForm
          productConfig={productConfig}
          onChange={handleProductConfigChange}
          productName={formData.name}
          productCode={formData.code}
          productDescription={formData.description}
          productStatus={formData.status}
          productTags={formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)}
          productVersion={formData.version}
          quotationScript={formData.quotationScript}
          onProductFieldChange={(field, value) => {
            if (field === 'tags') {
              setFormData({ ...formData, [field]: Array.isArray(value) ? value.join(', ') : String(value) });
            } else {
              setFormData({ ...formData, [field]: value });
            }
          }}
          errors={validationErrors}
        />

        {/* Validation Error Report */}
        {validationErrors.length > 0 && (
          <ValidationErrorReport 
            errors={validationErrors.map(err => ({
              ...err,
              field: err.path || 'unknown'
            }))}
            title="Please fix these issues before creating the product"
          />
        )}

        {/* Submit Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-end gap-4">
                  <Button
                    variant="outline"
                onClick={() => navigate('/admin/products')}
                disabled={loading}
                  >
                Cancel
                  </Button>
                          <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? 'Creating...' : 'Create Product'}
                        </Button>
            </div>
          </CardContent>
        </Card>
        </div>
    </div>
  );
} 